import React, { type Dispatch, type SetStateAction } from 'react';
import {
  type TrackToggleProps,
  useLocalParticipant,
  useRoomContext,
  useTrackToggle,
} from '@livekit/components-react';
import { Mic, MicOff, Video, VideoOff } from 'lucide-react';
import { Track } from 'livekit-client';
import type { ToggleSource } from '@livekit/components-core';
import { useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { cn } from '@/lib/utils';
import { usePretestDevices } from '@/contexts/PretestDevicesContext';

interface CustomTrackToggleProps<T extends ToggleSource> extends TrackToggleProps<T> {
  showIcon?: boolean;
  isAdmin?: boolean;
  /** État React parent (même clé que la modale de pré-test) — utilisé si les callbacks ci-dessous sont passés. */
  cameraEnabled?: boolean;
  microphoneEnabled?: boolean;
  onLocalCameraEnabledChange?: Dispatch<SetStateAction<boolean>>;
  onLocalMicrophoneEnabledChange?: Dispatch<SetStateAction<boolean>>;
  /**
   * Si false et caméra/micro pilotés par le parent : met à jour seulement l’état React (intent),
   * sans publier sur la room LiveKit à l’allumage — ex. intervenant pas encore « ajouté au live ».
   */
  publishLocalMediaToRoom?: boolean;
}

export const CustomTrackToggle: <T extends ToggleSource>(
  props: CustomTrackToggleProps<T> & React.RefAttributes<HTMLButtonElement>,
) => React.ReactNode = React.forwardRef(function CustomTrackToggle<T extends ToggleSource>(
  {
    showIcon = true,
    source,
    isAdmin: _isAdmin,
    cameraEnabled: cameraFromParent,
    microphoneEnabled: micFromParent,
    onLocalCameraEnabledChange,
    onLocalMicrophoneEnabledChange,
    publishLocalMediaToRoom = true,
    ...props
  }: CustomTrackToggleProps<T>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const pretest = usePretestDevices();
  const { buttonProps, enabled: trackToggleEnabled } = useTrackToggle({
    source,
    ...props,
  });
  const [isClient, setIsClient] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const { currentWorkspace } = useAppSelector((state: RootState) => state.workspace);
  const visualIdentity = currentWorkspace?.visualIdentity;

  const usesLocalMediaPower = source === Track.Source.Camera || source === Track.Source.Microphone;

  const isCameraControlled = Boolean(onLocalCameraEnabledChange && source === Track.Source.Camera);
  const isMicControlled = Boolean(
    onLocalMicrophoneEnabledChange && source === Track.Source.Microphone,
  );

  const localMediaEnabled = React.useMemo(() => {
    if (!usesLocalMediaPower) return trackToggleEnabled;
    if (source === Track.Source.Camera) {
      if (isCameraControlled && cameraFromParent !== undefined) {
        return cameraFromParent;
      }
      return localParticipant.isCameraEnabled;
    }
    if (isMicControlled && micFromParent !== undefined) {
      return micFromParent;
    }
    return localParticipant.isMicrophoneEnabled;
  }, [
    usesLocalMediaPower,
    source,
    localParticipant,
    trackToggleEnabled,
    isCameraControlled,
    isMicControlled,
    cameraFromParent,
    micFromParent,
  ]);

  React.useEffect(() => {
    setIsClient(true);
  }, []);

  const applyCameraOn = async () => {
    await localParticipant.setCameraEnabled(true);
    const id = pretest?.cameraId || room.getActiveDevice?.('videoinput') || undefined;
    if (id && room.switchActiveDevice) {
      try {
        await room.switchActiveDevice('videoinput', id);
      } catch (e) {
        console.error('CustomTrackToggle: switchActiveDevice videoinput', e);
      }
    }
  };

  const applyMicOn = async () => {
    await localParticipant.setMicrophoneEnabled(true);
    const id = pretest?.microphoneId || room.getActiveDevice?.('audioinput') || undefined;
    if (id && room.switchActiveDevice) {
      try {
        await room.switchActiveDevice('audioinput', id);
      } catch (e) {
        console.error('CustomTrackToggle: switchActiveDevice audioinput', e);
      }
    }
  };

  const handleLocalMediaClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    if (source === Track.Source.Camera && onLocalCameraEnabledChange) {
      setBusy(true);
      try {
        const currentlyOn = cameraFromParent ?? localParticipant.isCameraEnabled;
        const nextOn = !currentlyOn;
        if (nextOn) {
          if (publishLocalMediaToRoom) {
            await applyCameraOn();
          }
        } else {
          await localParticipant.setCameraEnabled(false);
        }
        onLocalCameraEnabledChange(nextOn);
      } catch (err) {
        console.error('CustomTrackToggle: toggle camera', err);
      } finally {
        setBusy(false);
      }
      return;
    }
    if (source === Track.Source.Microphone && onLocalMicrophoneEnabledChange) {
      setBusy(true);
      try {
        const currentlyOn = micFromParent ?? localParticipant.isMicrophoneEnabled;
        const nextOn = !currentlyOn;
        if (nextOn) {
          if (publishLocalMediaToRoom) {
            await applyMicOn();
          }
        } else {
          await localParticipant.setMicrophoneEnabled(false);
        }
        onLocalMicrophoneEnabledChange(nextOn);
      } catch (err) {
        console.error('CustomTrackToggle: toggle microphone', err);
      } finally {
        setBusy(false);
      }
      return;
    }
    setBusy(true);
    try {
      if (source === Track.Source.Camera) {
        if (localParticipant.isCameraEnabled) {
          await localParticipant.setCameraEnabled(false);
        } else {
          await applyCameraOn();
        }
        return;
      }
      if (source === Track.Source.Microphone) {
        if (localParticipant.isMicrophoneEnabled) {
          await localParticipant.setMicrophoneEnabled(false);
        } else {
          await applyMicOn();
        }
      }
    } catch (err) {
      console.error('CustomTrackToggle: toggle', err);
    } finally {
      setBusy(false);
    }
  };

  const getIcon = () => {
    if (!showIcon) return null;

    const iconColor = visualIdentity?.theme === 'DARK' ? 'text-black' : 'text-white';
    const enabled = localMediaEnabled;

    switch (source) {
      case Track.Source.Microphone:
        return enabled ? (
          <Mic className={cn('size-5', iconColor)} />
        ) : (
          <MicOff
            className={cn(
              'size-5',
              iconColor === 'text-white' ? 'text-[#FFC609]' : 'text-[#FFC609]',
            )}
          />
        );
      case Track.Source.Camera:
        return enabled ? (
          <Video className={cn('size-5', iconColor)} />
        ) : (
          <VideoOff className="size-5 text-[#FFC609]" />
        );
      default:
        return null;
    }
  };

  if (usesLocalMediaPower) {
    return (
      isClient && (
        <button
          ref={ref}
          type="button"
          onClick={handleLocalMediaClick}
          disabled={busy}
          className={cn(
            'p-2 rounded-lg cursor-pointer transition-colors disabled:opacity-50',
            visualIdentity?.theme === 'DARK'
              ? 'bg-white hover:bg-gray-100'
              : 'bg-dark-lighter hover:bg-[#4A4B5A]',
          )}
        >
          {getIcon()}
          {props.children}
        </button>
      )
    );
  }

  return (
    isClient && (
      <button
        ref={ref}
        {...buttonProps}
        className={cn(
          'p-2 rounded-lg cursor-pointer transition-colors',
          visualIdentity?.theme === 'DARK'
            ? 'bg-white hover:bg-gray-100'
            : 'bg-dark-lighter hover:bg-[#4A4B5A]',
        )}
      >
        {getIcon()}
        {props.children}
      </button>
    )
  );
});
